import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, del, errorMessage, get, patch, post } from '../../api/client';
import type { HistoryField, HistoryItem, HistoryTexts, Media, NewsLang } from '../../api/types';
import { useMe } from '../../auth';
import { IconAlert, IconArrowLeft, IconTrash } from '../../components/icons';
import { Field, formatDate, Spinner, Switch, useFeedback } from '../../components/ui';
import { useDraft, useNavigationGuard, useUnloadGuard } from '../../hooks';
import { tk, useT } from '../../i18n';
import { SaveBar } from '../model/SaveBar';
import { PmPhoto } from './photo';
import { emptyHistoryTexts, HISTORY_FIELDS, HISTORY_LIMITS, LANGS, langState, objectPosition } from './shared';

interface Form {
  year: string;
  texts: HistoryTexts;
  image: Media | null;
  crop: { x: number; y: number };
  visible: boolean;
}

const fromItem = (h: HistoryItem | null): Form =>
  h
    ? { year: h.year, texts: h.texts, image: h.image, crop: h.crop, visible: h.visible }
    : { year: '', texts: emptyHistoryTexts(), image: null, crop: { x: 0.5, y: 0.5 }, visible: true };

/** Los textos se traducen con t() al mostrarlos. */
const FIELD_LABELS: Record<HistoryField, { label: string; hint: string }> = {
  title: { label: tk('Título'), hint: tk('Corto: va en mayúsculas, en una sola línea, debajo del año.') },
  text: { label: tk('Texto'), hint: tk('Dos o tres frases. En escritorio va en una columna angosta a la izquierda.') },
};

type Crop = { x: number; y: number };

export function HistoryEditor() {
  const { id = 'nuevo' } = useParams();
  const isNew = id === 'nuevo';
  const { data, isPending, error } = useQuery({
    queryKey: ['pm-history', id],
    queryFn: async () => (await get<{ item: HistoryItem }>(`/admin/pm/history/${id}`)).item,
    enabled: !isNew,
  });
  if (!isNew && isPending) return <Spinner />;
  if (!isNew && (error || !data)) return <div className="notice error">{errorMessage(error)}</div>;
  return <HistoryForm key={id} item={isNew ? null : data!} />;
}

function HistoryForm({ item }: { item: HistoryItem | null }) {
  const { t, tr } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { toast, confirm } = useFeedback();
  const initial = useMemo(() => fromItem(item), [item]);
  const { draft: f, setDraft: setF, dirty, reset, markSaved } = useDraft<Form>(initial);
  const [lang, setLang] = useState<NewsLang>('en');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [leaving, setLeaving] = useState(false);
  useUnloadGuard(dirty && !leaving);
  useNavigationGuard(dirty && !leaving);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setF((prev) => ({ ...prev, [key]: value }));
  const setText = (l: NewsLang, field: HistoryField, value: string) =>
    setF((prev) => ({ ...prev, texts: { ...prev.texts, [l]: { ...prev.texts[l], [field]: value } } }));

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['pm-history', 'list'] });
    void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };

  const save = async () => {
    const submitted = f;
    if (!f.image) {
      setError(new ApiError(400, t('Falta la foto del hito')));
      return;
    }
    setBusy(true);
    setError(null);
    const body = { year: f.year.trim(), texts: f.texts, imageMediaId: f.image.id, crop: f.crop, visible: f.visible };
    try {
      if (!item) {
        const { item: created } = await post<{ item: HistoryItem }>('/admin/pm/history', body);
        qc.setQueryData(['pm-history', created.id], created);
        refresh();
        toast(t('Hito creado al final de la línea de tiempo. Se verá en el sitio después de publicar.'));
        // Sin la guarda de "cambios sin guardar": ya se guardó todo.
        flushSync(() => setLeaving(true));
        navigate(`/historia/${created.id}`, { replace: true });
        return;
      }
      const { item: updated } = await patch<{ item: HistoryItem }>(`/admin/pm/history/${item.id}`, body);
      markSaved(submitted);
      qc.setQueryData(['pm-history', item.id], updated);
      refresh();
      toast(t('Guardado. Se verá en el sitio después de publicar.'));
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, errorMessage(e));
      setError(err);
      // Si el error es de un texto, se abre la pestaña de su idioma.
      const bad = LANGS.find((l) => HISTORY_FIELDS.some((field) => err.field(`texts.${l.code}.${field}`)));
      if (bad) setLang(bad.code);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    const ok = await confirm(
      tr('¿Borrar el hito {title}? Sale del sitio en la próxima publicación. Si solo quieres sacarlo un tiempo, ocúltalo.', {
        title: <strong>{t('«{name}»', { name: `${item.year} · ${item.texts.en.title}` })}</strong>,
      }),
      { confirmLabel: t('Borrar'), danger: true },
    );
    if (!ok) return;
    try {
      await del(`/admin/pm/history/${item.id}`);
      toast(t('Hito borrado'));
      refresh();
      flushSync(() => setLeaving(true));
      navigate('/historia', { replace: true });
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  const errors = item?.issues.filter((i) => i.level === 'error') ?? [];
  const warnings = item?.issues.filter((i) => i.level === 'warning') ?? [];
  const textError = (l: NewsLang, field: HistoryField) => error?.field(`texts.${l}.${field}`);
  // La vista previa muestra el idioma de la pestaña; lo que falta, en inglés (como en el sitio).
  const shown = (field: HistoryField) => f.texts[lang][field].trim() || f.texts.en[field];

  return (
    <>
      <Link to="/historia" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
        <IconArrowLeft /> {t('Historia')}
      </Link>
      <header className="page-head">
        <div>
          <p className="kicker">{item ? t('Historia · hito') : t('Historia · hito nuevo')}</p>
          <h1>{[f.year.trim(), f.texts.en.title.trim()].filter(Boolean).join(' · ') || t('Nuevo hito')}</h1>
          {item && (
            <p className="faint" style={{ fontSize: 13 }}>
              {t('Última edición: {date}', { date: formatDate(item.updatedAt) })}
            </p>
          )}
        </div>
        <div className="actions">
          <Switch checked={f.visible} onChange={(v) => set('visible', v)} label={f.visible ? t('Visible en el sitio') : t('Oculto')} />
          {item && me?.role === 'ADMIN' && (
            <button className="btn btn-danger" onClick={() => void remove()}>
              <IconTrash /> {t('Borrar')}
            </button>
          )}
        </div>
      </header>

      {(errors.length > 0 || warnings.length > 0) && (
        <div className={`notice ${errors.length ? 'error' : 'warn'}`} style={{ marginBottom: 18, display: 'block' }}>
          <strong style={{ display: 'block', marginBottom: 6 }}>
            {errors.length ? t('Falta completar para poder publicarlo visible') : t('Avisos (no impiden publicar)')}
          </strong>
          <ul className="issues">
            {[...errors, ...warnings].map((i) => (
              <li key={i.message}>
                <IconAlert className="ico" style={{ color: i.level === 'error' ? 'var(--red)' : 'var(--warn)' }} />
                <span>{i.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && !error.errors.length && <div className="notice error" style={{ marginBottom: 18 }}>{error.message}</div>}

      <div className="news-editor">
        <section className="panel panel-pad stack">
          <div>
            <h2 className="section-title">{t('Foto y encuadre')}</h2>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {t('La foto va de fondo a toda la sección, detrás de un velo oscuro que deja leer el texto a la izquierda. Arrastra el recuadro para elegir qué parte se ve.')}
            </p>
          </div>
          <PmPhoto
            media={f.image}
            crop={f.crop}
            onMedia={(m) => setF((prev) => ({ ...prev, image: m, crop: { x: 0.5, y: 0.5 } }))}
            onCrop={(c) => set('crop', c)}
            error={error?.field('imageMediaId')}
            previews={(media, crop) => <TimelinePreviews media={media} crop={crop} year={f.year.trim() || '1902'} title={shown('title')} text={shown('text')} lang={lang} />}
          />
        </section>

        <div className="stack">
          <section className="panel panel-pad stack">
            <h2 className="section-title">{t('El hito')}</h2>
            <Field
              label={t('Año')}
              error={error?.field('year')}
              count={{ value: f.year, max: HISTORY_LIMITS.year }}
              hint={t('El número grande del hito: «1902», o «1930s» si no hay un año exacto. Igual en los seis idiomas.')}
            >
              {(id) => <input id={id} className="input history-year-input" value={f.year} placeholder="1902" onChange={(e) => set('year', e.target.value)} />}
            </Field>
            <p className="faint" style={{ margin: 0, fontSize: 13 }}>
              {item
                ? t('Su lugar en la línea de tiempo se cambia arrastrándolo en la lista de la historia.')
                : t('Un hito nuevo queda al final de la línea de tiempo; después se puede mover arrastrándolo en la lista.')}
            </p>
          </section>

          <section className="panel panel-pad">
            <div className="row between row-wrap" style={{ marginBottom: 6 }}>
              <h2 className="section-title" style={{ margin: 0 }}>
                {t('Textos')}
              </h2>
              <span className="faint" style={{ fontSize: 13 }}>
                {t('El inglés es obligatorio. Lo que quede vacío en otro idioma sale en inglés en el sitio.')}
              </span>
            </div>
            <div className="tabs lang-tabs" role="tablist">
              {LANGS.map((l) => {
                const state = langState(f.texts, l.code, HISTORY_FIELDS);
                const bad = HISTORY_FIELDS.some((field) => textError(l.code, field));
                return (
                  <button key={l.code} role="tab" className="tab" aria-selected={lang === l.code} onClick={() => setLang(l.code)}>
                    <span className={`lang-dot ${bad ? 'bad' : state}`} aria-hidden="true" />
                    {t(l.name)}
                  </button>
                );
              })}
            </div>
            <div className="stack">
              {HISTORY_FIELDS.map((field) => {
                const value = f.texts[lang][field];
                const fallback = lang !== 'en' ? f.texts.en[field] : '';
                const props = {
                  className: `${field === 'text' ? 'textarea' : 'input'} ${textError(lang, field) ? 'invalid' : ''}`,
                  value,
                  placeholder: fallback ? t('En inglés: {text}', { text: fallback }) : undefined,
                  onChange: (e: { target: { value: string } }) => setText(lang, field, e.target.value),
                };
                return (
                  <Field
                    key={`${lang}-${field}`}
                    label={lang === 'en' ? t(FIELD_LABELS[field].label) : t('{label} (opcional)', { label: t(FIELD_LABELS[field].label) })}
                    count={{ value, max: HISTORY_LIMITS[field] }}
                    error={textError(lang, field)}
                    hint={t(FIELD_LABELS[field].hint)}
                  >
                    {(id) => (field === 'text' ? <textarea id={id} rows={4} lang={lang} {...props} /> : <input id={id} lang={lang} {...props} />)}
                  </Field>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={() => void save()} onReset={reset} />
    </>
  );
}

/**
 * Cómo se ve el hito en la landing: en escritorio (la sección mide 1440 × 760 y la foto la cubre
 * entera, recortada arriba y abajo) y en el celular (la foto cubre la pantalla, cargada a la
 * derecha). Con el velo y los textos donde los pone el sitio.
 */
function TimelinePreviews({ media, crop, year, title, text, lang }: { media: Media; crop: Crop; year: string; title: string; text: string; lang: NewsLang }) {
  const { t } = useT();
  const photo = <img src={media.url} alt="" style={{ objectPosition: objectPosition(crop) }} />;
  return (
    <div className="tl-previews">
      <figure>
        <div className="pv pv-tl" lang={lang}>
          <div className="pv-tl-card">{photo}</div>
          <span className="pv-tl-veil" aria-hidden="true" />
          <span className="pv-tl-rule" aria-hidden="true" />
          <div className="pv-tl-year">{year}</div>
          <div className="pv-tl-title">{title}</div>
          <p className="pv-tl-text">{text}</p>
        </div>
        <figcaption>{t('En la landing (escritorio)')}</figcaption>
      </figure>
      <figure>
        <div className="pv pv-tl-m" lang={lang}>
          <div className="pv-tl-card">{photo}</div>
          <span className="pv-tl-veil" aria-hidden="true" />
          <div className="pv-tl-year">{year}</div>
          <div className="pv-tl-title">{title}</div>
          <p className="pv-tl-text">{text}</p>
        </div>
        <figcaption>{t('En el celular')}</figcaption>
      </figure>
    </div>
  );
}
